import React, { Fragment, useState } from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { closeModal } from '../../../reducers/modal'
import { hideCookiesInfo } from '../../../reducers/analytics'

import ModalContent from './core/ModalContent'
import Title from '../typography/Title'
import Button from '../Button'
import CollapsibleElement from '../CollapsibleElement'
import BulletList from '../lists/BulletList'
import { getAnalyticsStatus, updateAnalytics } from '../../../utils/analytics'
import { createTrackingCookie } from '../../../utils/tracking'

function DashedHr () {
  return <div className='hr--dashed col-9 fluid-mb3 mx-auto'>&nbsp;</div>
}

function CookieSection ({
  title,
  subtitle,
  description,
  descriptionAside,
  items,
  collapsible
}) {
  const [expanded, setExpanded] = useState(false)

  const descriptionContent = (
    <p
      className={classnames({
        'fluid-mb3': items
      })}
    >
      {description}
    </p>
  )

  function getCollapsibleContent () {
    return (
      <CollapsibleElement
        expanded={expanded}
        icon={expanded ? 'minus' : 'plus'}
        onClick={() => {
          setExpanded(e => !e)
        }}
        collapsibleContent={
          <Fragment>
            {subtitle && <div className='small mb1'>{subtitle}</div>}
            {description && descriptionContent}
            {items && (
              <BulletList
                itemExtraClass='cookie-section__bullet-item full-width'
                list={items}
                inlineCentered={false}
              />
            )}
          </Fragment>
        }
        btnLabel={
          <Title size={5} Element='p'>
            {title}
          </Title>
        }
        classNamePrefix='cookie-section'
      />
    )
  }

  function getRegularContent () {
    return (
      <div className='flex flex-wrap fluid-mbn3'>
        <div className='flex-grow fluid-mb3'>
          <Title size={5} Element='p' extraClass='mb1'>
            {title}
          </Title>
          {subtitle && <div className='small mb1'>{subtitle}</div>}
          {description && descriptionContent}
        </div>
        {descriptionAside && (
          <div className='flex-no-shrink fluid-mb3'>{descriptionAside}</div>
        )}
      </div>
    )
  }

  return (
    <div className='fluid-p3 border border1 rounded standard-radius border-clear-night-blue-transparentized fluid-mb3'>
      {collapsible ? getCollapsibleContent() : getRegularContent()}
    </div>
  )
}

function CookieBullet ({ title, isEnabled, onChange }) {
  return (
    <div className='sm-flex flex-wrap items-center justify-between full-width'>
      <div className='flex-grow'>{title}</div>
      <div className='cookie-section__bullet-item__buttons-container flex-no-shrink fluid-mrn2 fluid-mbn2'>
        <Button
          action
          selected={isEnabled}
          onClick={() => {
            onChange(true)
          }}
          extraClass='fluid-mr2 fluid-mb2'
        >
          {isEnabled ? 'Enabled' : 'Enable'}
        </Button>
        <Button
          action
          selected={!isEnabled}
          onClick={() => {
            onChange(false)
          }}
          extraClass='fluid-mr2 fluid-mb2'
        >
          {isEnabled ? 'Disable' : 'Disabled'}
        </Button>
      </div>
    </div>
  )
}

const SERVICES = [
  'google',
  'mixpanel',
  'hubspot',
  'googleAds',
  'bingAds',
  'twitterAds',
  'linkedinAds'
]

// Checks if services are all set to 'false' or 'true'.
// Return true, false or null when values are not aligned.
function checkAllCookiesSetToSameValue (enabledServices) {
  return Object.values(enabledServices).reduce((acc, value) => {
    if (acc === undefined) {
      return value
    } else if (acc === null) {
      return null
    } else if (value === acc) {
      return acc
    } else {
      return null
    }
  }, undefined)
}

function CookiesModal ({ closeModal, hideCookiesInfo }) {
  const analyticsStatus = getAnalyticsStatus()
  const globallyEnabled = analyticsStatus.globallyEnabled

  const initialServicesState = SERVICES.reduce((acc, service) => {
    acc[service] = globallyEnabled || analyticsStatus.services.includes(service)
    return acc
  }, {})

  const [enabledServices, setEnabledServices] = useState(initialServicesState)

  function setAll (value) {
    setEnabledServices(s => {
      let updatedServices = {}
      Object.keys(enabledServices).forEach(service => {
        updatedServices[service] = value
      })
      return updatedServices
    })
  }

  function setOne (item) {
    return value => {
      const services = {
        ...enabledServices,
        [item]: value
      }
      setEnabledServices(services)
    }
  }

  const areCookieValuesAligned = checkAllCookiesSetToSameValue(enabledServices)

  function onSave () {
    createTrackingCookie(
      areCookieValuesAligned === true,
      Object.entries(enabledServices)
        .filter(([service, enabled]) => enabled)
        .map(([service]) => service)
    )
    updateAnalytics(analyticsStatus, getAnalyticsStatus())
    // updateAnalytics can cause a page refresh - so the code below this comment is not guaranteed to execute.
    closeModal()
    hideCookiesInfo()
  }

  const content = (
    <Fragment>
      <Title size={3} Element='p' extraClass='fluid-mb3'>
        Cookies Settings
      </Title>
      <CookieSection
        title='All'
        description='Enable or disable all cookies.'
        descriptionAside={
          <Fragment>
            <Button
              action
              selected={areCookieValuesAligned === true}
              extraClass='fluid-mr2'
              onClick={() => setAll(true)}
            >
              {areCookieValuesAligned === true ? 'Enabled' : 'Enable'}
            </Button>
            <Button
              action
              selected={areCookieValuesAligned === false}
              onClick={() => setAll(false)}
            >
              {areCookieValuesAligned === false ? 'Disabled' : 'Disable'}
            </Button>
          </Fragment>
        }
      />
      <DashedHr />
      <CookieSection
        collapsible
        title='Necessary cookies'
        subtitle='1 day to 12 months'
        description='Used to remember your opt-in preferences and dismissed banners, and, if you create an account, to remember your opt-in preferences, keep you logged into your account, and save drafts. These cookies are technically necessary for you to enjoy the Websites and cannot be opted-out from.'
        items={['Snips']}
      />
      <CookieSection
        collapsible
        title='Analytics cookies'
        subtitle='1 day to 12 months'
        description='For aggregated statistics about website visits & interaction, type of browser, viewport dimensions and operating system used. Helps us understand how our websites are being used and how effective our marketing campaigns are, so we can improve.'
        items={[
          <CookieBullet
            title='Google Analytics'
            isEnabled={enabledServices.google}
            onChange={setOne('google')}
          />,
          <CookieBullet
            title='Mixpanel'
            isEnabled={enabledServices.mixpanel}
            onChange={setOne('mixpanel')}
          />
        ]}
      />
      <CookieSection
        collapsible
        title='Enterprise cookies'
        description='Used to facilitate sales activities and tailor communication. These cookies are dropped by our trusted service provider Hubspot to feed into the enterprise analytics and customer management tools we use.'
        items={[
          <CookieBullet
            title='Hubspot'
            isEnabled={enabledServices.hubspot}
            onChange={setOne('hubspot')}
          />
        ]}
      />
      <CookieSection
        collapsible
        title='Advertising (targeting) cookies'
        description='Used to deliver Snips ad content on third party websites, track the performance of our ad campaigns, make ad messages more relevant to you and your interest, and prevent the same ad from appearing over and over again. These cookies are dropped by third party service providers.'
        items={[
          <CookieBullet
            title='Google Ads'
            isEnabled={enabledServices.googleAds}
            onChange={setOne('googleAds')}
          />,
          <CookieBullet
            title='Bing Ads'
            isEnabled={enabledServices.bingAds}
            onChange={setOne('bingAds')}
          />,
          <CookieBullet
            title='Twitter Ads'
            isEnabled={enabledServices.twitterAds}
            onChange={setOne('twitterAds')}
          />,
          <CookieBullet
            title='LinkedIn Ads'
            isEnabled={enabledServices.linkedinAds}
            onChange={setOne('linkedinAds')}
          />
        ]}
      />
    </Fragment>
  )

  const footerContent = (
    <div className='flex items-center'>
      <Button
        variation='outline-deep-night-blue'
        extraClass='fluid-mr3'
        onClick={closeModal}
      >
        Cancel
      </Button>
      <Button variation='blue' onClick={onSave}>
        Save
      </Button>
    </div>
  )

  return (
    <ModalContent
      content={<div className='modal__content fluid-mb4'>{content}</div>}
      footerContent={footerContent}
    />
  )
}

export default connect(
  null,
  { closeModal, hideCookiesInfo }
)(CookiesModal)
